import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Percent, Crown, Coins, Gift, Rocket, Key } from 'lucide-react';
import { STAKING_REWARDS_WITH_RULE } from '@/lib/stakingRules';

const utilities = [
  {
    icon: Percent,
    title: 'Trading Fee Discounts',
    description: 'Hold $BZX to unlock tiered discounts on trading fees across BITZX Exchange.',
    size: 'large',
    color: 'from-[#9C7941] to-[#EBD38D]',
  },
  {
    icon: Crown,
    title: 'VIP Membership',
    description: 'Exclusive VIP tiers with premium benefits, priority support, and early feature access.',
    size: 'small',
    color: 'from-[#EBD38D] to-[#D5D5D0]',
  },
  {
    icon: Coins,
    title: 'Staking Rewards',
    description: STAKING_REWARDS_WITH_RULE,
    size: 'small',
    color: 'from-[#9C7941] to-[#4A4B50]',
  },
  {
    icon: Gift,
    title: 'Referral Rewards',
    description: 'Invite friends and earn $BZX rewards for every successful referral to our ecosystem.',
    size: 'small',
    color: 'from-[#EBD38D] to-[#9C7941]',
  },
  {
    icon: Rocket,
    title: 'Launchpad Access',
    description: 'Get exclusive early access to new token launches and IDO opportunities on BITZX Exchange.',
    size: 'small',
    color: 'from-[#D5D5D0] to-[#9C7941]',
  },
  {
    icon: Key,
    title: 'Premium Ecosystem',
    description: 'Unlock advanced trading tools, analytics, and features reserved for $BZX holders.',
    size: 'large',
    color: 'from-[#9C7941] to-[#EBD38D]',
  },
];

export const Utility = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section
      id="utility"
      ref={ref}
      className="py-24 md:py-32 xl:py-36 relative overflow-hidden bg-[#0a0b0d]"
      data-testid="utility-section"
    >
      {/* Background Elements */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#9C7941]/5 rounded-full blur-3xl" />
      
      <div className="max-w-7xl mx-auto px-6 md:px-10 xl:px-16 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block text-[#EBD38D] text-sm font-semibold tracking-wider uppercase mb-4">
            Token Utility
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Real Utility, Real Value
          </h2>
          <p className="text-[#D5D5D0] text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
            $BZX isn't just a token — it's your key to the entire BITZX ecosystem. 
            From trading benefits to exclusive access, discover what holding $BZX can do for you.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 xl:gap-10">
          {utilities.map((utility, index) => (
            <motion.div
              key={utility.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`card-glass p-8 relative group overflow-hidden ${
                utility.size === 'large' ? 'lg:col-span-1 row-span-1' : ''
              }`}
              data-testid={`utility-card-${index}`}
            >
              {/* Gradient Overlay on Hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${utility.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} />
              
              {/* Icon */}
              <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${utility.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <utility.icon size={28} className="text-[#1B1E20]" />
              </div>

              {/* Content */}
              <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#EBD38D] transition-colors">
                {utility.title}
              </h3>
              <p className="text-[#8A8B90] leading-relaxed">
                {utility.description}
              </p>

              {/* Decorative Line */}
              <div className="absolute bottom-0 left-0 w-0 h-1 bg-gradient-to-r from-[#9C7941] to-[#EBD38D] group-hover:w-full transition-all duration-500" />
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-8 text-center"
        >
          <p className="text-[#6B6D75] text-sm">
            More utility features will be unveiled as our ecosystem grows
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Utility;
